import { animate, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const INDUSTRIES = ["B2B", "B2C", "Real Estate", "Retail", "Finance", "Fintech", "SaaS", "E-commerce"];

const ease = [0.16, 1, 0.3, 1];

const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
};

const CountUp = ({ to }) => {
    const [n, setN] = useState(0);
    useEffect(() => {
        const c = animate(0, to, { duration: 1.8, delay: 0.6, ease: [0.22, 1, 0.36, 1], onUpdate: (v) => setN(Math.round(v)) });
        return () => c.stop();
    }, [to]);
    return <span className="tabular-nums">{n}</span>;
};

const Chip = ({ label }) => {
    const x = useSpring(useMotionValue(0), { stiffness: 150, damping: 12, mass: 0.4 });
    const y = useSpring(useMotionValue(0), { stiffness: 150, damping: 12, mass: 0.4 });
    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        x.set((e.clientX - (r.left + r.width / 2)) * 0.25);
        y.set((e.clientY - (r.top + r.height / 2)) * 0.25);
    };
    const reset = () => {
        x.set(0);
        y.set(0);
    };
    return (
        <motion.span
            style={{ x, y }}
            onMouseMove={onMove}
            onMouseLeave={reset}
            whileHover={{ rotate: -2, scale: 1.04 }}
            data-testid={`hero-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="rounded-full border border-[rgba(45,42,38,0.12)] bg-cream/80 px-5 py-2.5 text-sm md:text-base font-body font-medium text-text-primary cursor-default select-none hover:border-coral/60 hover:bg-cream transition-colors"
        >
            {label}
        </motion.span>
    );
};

const Letters = ({ word, start }) => (
    <span className="block" aria-hidden>
        {word.split("").map((ch, i) => (
            <span key={i} className="inline-block overflow-hidden align-bottom pb-[0.06em] -mb-[0.06em]">
                <motion.span
                    className="inline-block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, ease, delay: start + i * 0.06 }}
                >
                    {ch}
                </motion.span>
            </span>
        ))}
    </span>
);

const DrawnUnderline = ({ children }) => (
    <span className="relative inline-block text-text-primary font-medium" data-testid="hero-underline">
        {children}
        <svg aria-hidden viewBox="0 0 200 12" preserveAspectRatio="none" className="absolute left-0 -bottom-2 h-3 w-full overflow-visible">
            <motion.path
                d="M2 8 C 30 -2, 60 14, 90 6 S 150 -2, 198 8"
                stroke="#F28C81"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.1, ease: [0.65, 0, 0.35, 1], delay: 1.05 }}
            />
        </svg>
    </span>
);

const Stat = ({ value, label, testid }) => (
    <div data-testid={testid}>
        <div className="font-heading text-5xl md:text-6xl text-text-primary leading-none">
            <CountUp to={Number(value)} />
            <span className="text-coral">+</span>
        </div>
        <div className="mt-3 font-body text-base md:text-lg text-text-secondary">{label}</div>
    </div>
);

const HeroAside = () => (
    <motion.div
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.85, ease, delay: 0.5 }}
        className="lg:col-span-5 lg:pl-8"
    >
        <div className="flex items-center gap-8 md:gap-10" data-testid="hero-stats">
            <Stat value="8" label="Years designing" testid="hero-stat-years" />
            <span aria-hidden className="h-16 w-px bg-[rgba(45,42,38,0.15)]" />
            <Stat value="40" label="Brands & Creative Projects" testid="hero-stat-projects" />
        </div>

        <div className="mt-14 md:mt-20" data-testid="hero-industries">
            <div className="flex items-end gap-2">
                <span className="font-accent text-coral text-3xl md:text-4xl rotate-[-3deg]">working across</span>
                <svg aria-hidden viewBox="0 0 70 40" className="h-9 w-16 text-coral -mb-1" fill="none">
                    <path d="M4 14 C 20 -2, 50 0, 62 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M54 26 L62 32 L65 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 max-w-lg">
                {INDUSTRIES.map((ind) => (
                    <Chip key={ind} label={ind} />
                ))}
            </div>
            <div className="mt-8 inline-flex items-center gap-3 rounded-full bg-cream/80 border border-[rgba(45,42,38,0.1)] px-5 py-2.5" data-testid="hero-location">
                <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-coral opacity-70 animate-ping" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-coral" />
                </span>
                <span className="font-accent text-coral text-2xl md:text-[26px] -rotate-[2deg]">currently in Bay Area</span>
            </div>
        </div>
    </motion.div>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yBlob1 = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const yBlob2 = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative overflow-hidden min-h-[100svh] flex items-center pt-28 md:pt-28 pb-16 md:pb-20"
        >
            <motion.div style={{ y: yBlob1 }} aria-hidden className="absolute -top-20 -left-32 h-[420px] w-[420px] rounded-full bg-coral/30 blur-3xl animate-blob" />
            <motion.div style={{ y: yBlob2 }} aria-hidden className="absolute top-40 -right-24 h-[360px] w-[360px] rounded-full bg-butter/40 blur-3xl animate-blob" />
            <div aria-hidden className="absolute bottom-0 right-1/4 h-[260px] w-[260px] rounded-full bg-sage/40 blur-3xl animate-blob" />

            <div className="relative w-full max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-center">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease, delay: 0.1 }}
                        className="font-heading font-medium text-2xl md:text-3xl text-text-primary"
                        data-testid="hero-greeting"
                    >
                        Hi, <span className="font-accent text-coral text-3xl md:text-4xl inline-block -rotate-[3deg]">it’s</span>
                    </motion.p>

                    <h1
                        aria-label="Mona Saedi"
                        className="mt-1 font-heading font-semibold tracking-tight leading-[0.9] text-[22vw] sm:text-[17vw] lg:text-[9.5rem] xl:text-[10.5rem] text-text-primary"
                        data-testid="hero-title"
                    >
                        <Letters word="Mona" start={0.15} />
                        <Letters word="Saedi" start={0.4} />
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.85, ease, delay: 0.3 }}
                        className="mt-8 font-heading text-xl sm:text-2xl lg:text-[26px] font-light text-text-primary leading-[1.4] max-w-2xl"
                        data-testid="hero-subtitle"
                    >
                        I’m a <DrawnUnderline>Visual &amp; Brand Designer</DrawnUnderline>{" "}
                        creating distinctive brand identities, scalable visual systems, and engaging digital
                        experiences.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease, delay: 0.42 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#work"
                            onClick={scrollTo("#work")}
                            data-testid="hero-cta-work"
                            className="pill-btn inline-flex items-center gap-2 rounded-full bg-[#2D2A26] text-cream px-8 py-4 font-body font-semibold"
                        >
                            See selected work <span aria-hidden>↘</span>
                        </a>
                    </motion.div>
                </div>

                <HeroAside />
            </div>
        </section>
    );
};

export default Hero;
